import React, { useState, useEffect } from "react";
import { SlidersHorizontal, AlertCircle, Cpu, CheckCircle2, XCircle } from "lucide-react";
import PageHeader from "../../../components/PageHeader";
import LoadingScreen from "../../../components/LoadingScreen";
import StatusBadge from "../../../components/StatusBadge";
import { getBillingFeatureState, getBillingPilotDashboard } from "../../../services/settings";

const AsaasIntegration = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [featureState, setFeatureState] = useState(null);
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const [featureRes, dashboardRes] = await Promise.all([
          getBillingFeatureState().catch(() => ({ data: null })),
          getBillingPilotDashboard().catch(() => ({ data: null }))
        ]);
        if (isMounted) {
          setFeatureState(featureRes.data);
          setDashboard(dashboardRes.data);
        }
      } catch (err) {
        if (isMounted) setError(err.message || "Erro ao carregar dados do Asaas");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, []);

  if (loading) {
    return <LoadingScreen message="Carregando status da integração Asaas..." />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb="Financeiro & Integrações"
        title="Integração Gateway Asaas"
        subtitle="Monitoramento do gateway de pagamentos e feature flags do módulo financeiro"
      />

      {error && (
        <div className="p-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs font-semibold flex items-center gap-3">
          <AlertCircle size={18} /> {error}
        </div>
      )}

      {featureState && (
        <div className="neumorphic-card rounded-2xl p-6 border border-white/5 space-y-4">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <Cpu size={18} className="text-purple-400" /> Feature Flags Financeiras
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: "Piloto", active: featureState.pilotEnabled },
              { label: "Rollout", active: featureState.rolloutEnabled },
              { label: "Sandbox", active: featureState.asaasSandboxEnabled },
              { label: "Webhook", active: featureState.webhookEnabled },
              { label: "Entitlements", active: featureState.entitlementsEnabled },
              { label: "Enforcement", active: featureState.enforcementEnabled }
            ].map((flag, idx) => (
              <div key={idx} className="p-3 rounded-xl bg-white/5 border border-white/5 flex flex-col justify-between space-y-2">
                <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">{flag.label}</span>
                <StatusBadge status={flag.active ? "active" : "inactive"} label={flag.active ? "Ativado" : "Desativado"} />
              </div>
            ))}
          </div>
        </div>
      )}

      {dashboard && (
        <div className="neumorphic-card rounded-2xl p-6 border border-white/5 space-y-4">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <SlidersHorizontal size={18} className="text-purple-400" /> Dashboard do Piloto Asaas
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(dashboard).map(([key, value]) => (
              <div key={key} className="p-3.5 rounded-xl bg-white/5 border border-white/5 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-300 font-mono">{key}</span>
                <span className="text-xs font-bold text-purple-300">{String(value ?? "—")}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!featureState && !dashboard && (
        <div className="neumorphic-card rounded-2xl p-8 border border-white/5 text-center space-y-2">
          <h3 className="text-base font-bold text-white">Aguardando Dados do Gateway</h3>
          <p className="text-xs text-gray-400">
            Nenhum retorno recebido dos endpoints de integração do Asaas.
          </p>
        </div>
      )}
    </div>
  );
};

export default AsaasIntegration;
